/*
 * Native PDP components (sections/pdp-*.liquid).
 * <pdp-slider>      scroll-snap slider with arrows + pagination dots
 * <pdp-gallery>     syncs the product media slider with its thumbnail strip
 * <pdp-lazy-video>  starts muted background videos only when they near the viewport
 */

if (!customElements.get('pdp-slider')) {
  class PdpSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(':scope > [data-slider-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.prevButton = this.querySelector(':scope > [data-slider-prev]');
      this.nextButton = this.querySelector(':scope > [data-slider-next]');
      this.dots = this.querySelector(':scope > [data-slider-dots]');
      this.index = 0;
      this.pageCount = 0;

      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots?.addEventListener('click', (event) => {
        const dot = event.target.closest('button[data-index]');
        if (dot) this.goTo(Number(dot.dataset.index));
      });
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.addEventListener('shopify:block:select', (event) => {
        const slide = this.slides.find((item) => item.contains(event.target));
        if (slide) this.goTo(this.indexOfSlide(slide), 'auto');
      });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    get step() {
      const [first, second] = this.slides;
      return second ? second.offsetLeft - first.offsetLeft : this.track.clientWidth;
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    indexOfSlide(slide) {
      return Math.min(this.slides.indexOf(slide), this.pageCount - 1);
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    goTo(index, behavior = 'smooth') {
      const target = Math.max(0, Math.min(index, this.pageCount - 1));
      this.track.scrollTo({ left: Math.min(target * this.step, this.maxScroll), behavior });
    }

    renderDots() {
      if (!this.dots) return;
      this.dots.replaceChildren(
        ...Array.from({ length: this.pageCount > 1 ? this.pageCount : 0 }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.index = i;
          dot.setAttribute('aria-label', `${i + 1} / ${this.pageCount}`);
          return dot;
        })
      );
    }

    update() {
      if (!this.slides.length) return;
      const max = this.maxScroll;
      const position = this.track.scrollLeft;
      const pageCount = max < 2 ? 1 : Math.ceil(max / this.step - 0.01) + 1;

      if (pageCount !== this.pageCount) {
        this.pageCount = pageCount;
        this.renderDots();
      }

      const atEnd = position >= max - 2;
      const index = atEnd ? pageCount - 1 : Math.round(position / this.step);

      if (this.prevButton) this.prevButton.disabled = position <= 2;
      if (this.nextButton) this.nextButton.disabled = atEnd;
      this.dots?.querySelectorAll('button').forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.slides.forEach((slide) => {
        const visible = slide.offsetLeft + slide.offsetWidth > position + 1 && slide.offsetLeft < position + this.track.clientWidth - 1;
        slide.toggleAttribute('aria-hidden', !visible);
      });

      if (index !== this.index) {
        this.index = index;
        this.dispatchEvent(new CustomEvent('pdp-slider:change', { detail: { index }, bubbles: true }));
      }
    }
  }

  customElements.define('pdp-slider', PdpSlider);
}

if (!customElements.get('pdp-gallery')) {
  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbStrip = this.querySelector('[data-gallery-thumbs]');
      if (!this.main || !this.thumbStrip) return;

      this.thumbs = Array.from(this.thumbStrip.querySelectorAll('[data-index]'));
      this.thumbStrip.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-index]');
        if (thumb) this.main.goTo(Number(thumb.dataset.index));
      });
      this.main.addEventListener('pdp-slider:change', (event) => this.setActive(event.detail.index));
    }

    setActive(index) {
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // Keep the active thumbnail in view without scrolling the page vertically.
      const thumb = this.thumbs[index];
      if (!thumb) return;
      const strip = this.thumbStrip;
      const left = thumb.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft) strip.scrollTo({ left });
      else if (left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left + thumb.offsetWidth - strip.clientWidth });
      }
    }
  }

  customElements.define('pdp-gallery', PdpGallery);
}

if (!customElements.get('pdp-lazy-video')) {
  class PdpLazyVideo extends HTMLElement {
    connectedCallback() {
      const video = this.querySelector('video[data-src]');
      if (!video) return;

      const load = () => {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
        video.play?.().catch(() => {});
      };

      if (!('IntersectionObserver' in window)) return load();

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          load();
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  customElements.define('pdp-lazy-video', PdpLazyVideo);
}

/**
 * Adds the variant in `form` to the cart, then sends the shopper to checkout.
 * Used by <pdp-buy-now> (main button) and <pdp-sticky-atc> when set to "go to checkout".
 * Returns an error message, or nothing when it is navigating to checkout.
 */
async function pdpBuyNow(form, button) {
  if (!form || button?.getAttribute('aria-busy') === 'true') return;
  const root = window.Shopify?.routes?.root || '/';
  const body = new FormData(form);
  body.delete('return_to'); // no-JS fallback field; the AJAX endpoint must answer with JSON

  // Button text: label -> "Adding..." -> "Thank you!" (kept while checkout loads).
  const label = button?.querySelector(':scope > span') || button;
  if (label && label.dataset.pdpLabel === undefined) label.dataset.pdpLabel = label.textContent;
  const setText = (text) => { if (label && text) label.textContent = text; };

  button?.setAttribute('aria-busy', 'true');
  button?.classList.add('is-loading');
  setText(button?.dataset.addingText);
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    setText(button?.dataset.addedText);
    window.location.href = `${root}checkout`;
  } catch (error) {
    pdpResetBuyButton(button);
    return error.message;
  }
}

function pdpResetBuyButton(button) {
  if (!button) return;
  const label = button.querySelector(':scope > span') || button;
  if (label.dataset.pdpLabel !== undefined) label.textContent = label.dataset.pdpLabel;
  button.removeAttribute('aria-busy');
  button.classList.remove('is-loading');
}

// Coming back from checkout with the browser's back button restores the page from cache:
// put the buttons back to their normal label.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) document.querySelectorAll('[data-adding-text][aria-busy="true"]').forEach(pdpResetBuyButton);
});

if (!customElements.get('pdp-buy-now')) {
  class PdpBuyNow extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
        const message = await pdpBuyNow(this.form, this.querySelector('[type="submit"]'));
        if (message && wrapper) {
          wrapper.querySelector('.product-form__error-message').textContent = message;
          wrapper.hidden = false;
        }
      });
    }
  }

  customElements.define('pdp-buy-now', PdpBuyNow);
}

if (!customElements.get('pdp-sticky-atc')) {
  class PdpStickyAtc extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.target);
      if (!this.target || !('IntersectionObserver' in window)) return;

      // Show only once the main buy button has scrolled above the viewport. The root is
      // extended far below the screen so the button counts as "intersecting" anywhere
      // below the top edge; a single jump past it (fling, anchor link) still fires.
      this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting), {
        rootMargin: '0px 0px 100000px 0px',
      });
      this.observer.observe(this.target);

      this.addEventListener('shopify:block:select', () => this.toggle(true));
      this.addEventListener('shopify:block:deselect', () => this.toggle(false));
      this.querySelector('[data-sticky-checkout]')?.addEventListener('click', (event) => this.buyNow(event.currentTarget));
    }

    // Adds the variant currently selected in the main product form, then goes to checkout.
    async buyNow(button) {
      const error = this.querySelector('.pdp-sticky-atc__error');
      if (error) error.hidden = true;
      const message = await pdpBuyNow(document.getElementById(this.dataset.form), button);
      if (message && error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    toggle(visible) {
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    }
  }

  customElements.define('pdp-sticky-atc', PdpStickyAtc);
}

// Theme editor: open a collapsible row when its block is selected.
document.addEventListener('shopify:block:select', (event) => {
  const details = event.target.closest?.('details.pdp-collapsible') || event.target.querySelector?.('details.pdp-collapsible');
  if (details) details.open = true;
});
